import { IconSearch, IconX } from '@tabler/icons-react';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Button } from '@zorro/design-system/v2/Button';
import { Chip } from '@zorro/design-system/v2/Chip';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Search } from '@zorro/design-system/v2/Search';
import { Title } from '@zorro/design-system/v2/Title';
import {
  DataTable,
  Flex,
  Group,
  IDataTableProps,
  Icon,
  Space,
  Text,
  brand,
} from '@zorro/web/design-system';
import { useDebouncedValue } from '@zorro/web/ui-utils';
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '../Analytics';
import { DownloadButton } from '../DownloadButton';
import SkeletonDataTable from './SkeletonDataTable';

export type AvailableView = {
  value: string;
  label: string;
  count: number;
};

type SearchableDataTableProps = {
  componentsAfterSearch?: ReactNode;
  componentsAlignedRightToHeading?: ReactNode;
  componentsRightDownloadButton?: ReactNode;
  componentsLeftToDownloadButton?: ReactNode;
  onDownload?: () => void;
  shouldShowDownload?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
  isSearchLoading?: boolean;
  availableViews?: AvailableView[];
  currentView?: string;
  setCurrentView?: (view: string) => void;
  filtersCount?: number;
  clearFilters?: () => void;
  isSearchEnabled?: boolean;
  onSearchIconClick?: () => void;
  searchPlaceholder?: string;
  clearSelectedRecords?: () => void;
  heading?: string | ReactElement;
  subHeading?: string | ReactElement;
} & IDataTableProps;

export function SearchableDataTable({
  componentsAfterSearch,
  componentsAlignedRightToHeading,
  componentsRightDownloadButton,
  componentsLeftToDownloadButton,
  searchValue = '',
  onSearchChange,
  onDownload,
  shouldShowDownload = true,
  isLoading = false,
  availableViews,
  currentView,
  setCurrentView,
  filtersCount = 0,
  clearFilters,
  clearSelectedRecords,
  heading,
  subHeading,
  isSearchEnabled = false,
  onSearchIconClick,
  searchPlaceholder = 'Search employees',
  records,
  ...rest
}: SearchableDataTableProps) {
  const { sendEvent } = useAnalytics();
  const selectedRecordsCount = rest?.selectedRecords?.length || 0;
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(
    Boolean(searchValue)
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(searchValue);
  const { debounced: debouncedInputValue } = useDebouncedValue({
    value: inputValue,
    wait: 500,
  });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onSearchChange(debouncedInputValue);
  }, [debouncedInputValue]);

  useEffect(() => {
    if (searchValue) {
      setIsSearchOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        pr="var(--space-24)"
        style={{ width: '100%' }}
      >
        <Group align="center" h="2.5rem" w="100%" gap="var(--space-12)">
          {typeof heading === 'string' ? <Title>{heading}</Title> : heading}
          {isSearchEnabled &&
            (isSearchOpen ? (
              <div style={{ width: 280, flexShrink: 0 }}>
                <Search
                  ref={inputRef}
                  onClear={() => {
                    setInputValue('');
                    onSearchChange('');
                    setIsSearchOpen(false);
                  }}
                  placeholder={searchPlaceholder}
                  value={inputValue}
                  onInput={(args) => {
                    setInputValue(args.currentTarget.value);
                  }}
                  onBlur={(event) => {
                    if (!event.currentTarget.value) {
                      setIsSearchOpen(false);
                    }
                  }}
                />
              </div>
            ) : (
              <div style={{ paddingLeft: 4 }}>
                <ActionIcon
                  onClick={() => {
                    void sendEvent('click_search');
                    setIsSearchOpen(true);
                    if (onSearchIconClick) {
                      onSearchIconClick();
                    }
                  }}
                  id="search-icon"
                >
                  <Icon
                    icon={IconSearch}
                    color={brand.zorroGray800}
                    style={{
                      color: brand.zorroGray800,
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </ActionIcon>
              </div>
            ))}
        </Group>
        {componentsAlignedRightToHeading}
      </Flex>
      {subHeading && (
        <Group align="center" mb="md">
          {typeof subHeading === 'string' ? (
            <Text c="zorroGray.4">{subHeading}</Text>
          ) : (
            subHeading
          )}
        </Group>
      )}
      <Group wrap="nowrap" align="start">
        <Group
          mt="xs"
          gap="xs"
          role="radiogroup"
          style={{ flex: 1, minWidth: 0 }}
        >
          {availableViews?.map((view) => (
            <Chip
              key={view.value}
              value={view.value}
              label={view.label}
              count={view.count}
              isSelected={currentView === view.value}
              onSelect={setCurrentView}
            />
          ))}
          {filtersCount > 0 && clearFilters && (
            <Group>
              <Divider orientation="vertical" />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => clearFilters()}
                rightSection={
                  <Icon icon={IconX} size="16px" color={brand.zorroGray800} />
                }
              >{`Filters: ${filtersCount}`}</Button>
            </Group>
          )}
        </Group>
        <Group ml="auto">{componentsAfterSearch}</Group>

        <Flex justify="space-between" align="end" pr="1.5rem" mt="xs">
          <Group
            h="100%"
            gap="var(--space-12)"
            justify="right"
            pos="relative"
            wrap="nowrap"
          >
            {selectedRecordsCount !== 0 && (
              <Flex align="center" gap="0.5rem">
                <Text c="zorroCoal.9" size="sm" style={{ flexShrink: 0 }}>
                  {selectedRecordsCount} selected
                </Text>
                <ActionIcon onClick={clearSelectedRecords}>
                  <Icon icon={IconX} size="16px" color={brand.zorroGray800} />
                </ActionIcon>
              </Flex>
            )}
            {componentsLeftToDownloadButton}
            {shouldShowDownload && onDownload && (
              <DownloadButton onDownload={onDownload} isDisabled={isLoading} />
            )}
            {componentsRightDownloadButton}
          </Group>
        </Flex>
      </Group>
      <Space h="md" />

      {records ? (
        <DataTable
          reduceHeightBy={280}
          records={records}
          isFetching={isLoading}
          {...rest}
        />
      ) : (
        <SkeletonDataTable />
      )}
    </>
  );
}
